import { useEffect } from "react";

import useHelpers from "Hooks/useHelpers";
import useUserContext from "Hooks/useUserContext";

/** Check if user has valid session by checking JWT with /api/me endpoint and updates user context
 * @returns Nothing
 * @example useIsAuthorized();
 */
export default function useIsAuthorized() {
    const [userContext, onSetUserContext] = useUserContext();

    const { getCookieValue, handleMeEndpointOkResponse } = useHelpers();

    // Get company_id from cookie
    useEffect(() => {
        let ignore = false;

        if (
            userContext.companyId ||
            !userContext.environment ||
            userContext.isAuthenticated
        ) {
            return;
        }

        const company_id = getCookieValue(
            `${userContext.environment}_company_id`
        );

        if (!ignore && company_id) {
            onSetUserContext({
                companyId: company_id,
            });
        }

        return () => {
            ignore = true;
        };
    }, [
        getCookieValue,
        onSetUserContext,
        userContext.companyId,
        userContext.environment,
        userContext.isAuthenticated,
    ]);

    // Fetch environment
    useEffect(() => {
        let ignore = false;

        if (userContext.environment) {
            return;
        }

        // Check environment from /api/environment endpoint
        getEnvironment();

        async function getEnvironment() {
            const response = await fetch("/api/environment");
            if (response.ok && !ignore) {
                const data = await response.json();
                console.log("Environment is set to: ", data.environment);
                onSetUserContext({
                    environment: data.environment,
                    gitHash: data.git_hash,
                    buildDate: data.build_date,
                    portalVersion: data.portal_version,
                    machineName: data.machine_name,
                });
            } else if (!ignore) {
                console.log("Error retrieving environment: ", response);
            }
        }

        return () => {
            ignore = true;
        };
    }, [
        onSetUserContext,
        userContext.environment,
    ]);

    // Validate JWT
    useEffect(() => {
        let ignore = false;

        if (userContext.isAuthenticated !== undefined) { return; }
        
        validateJwt();

        async function validateJwt() {
            const response = await fetch("/api/me", {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok && !ignore) {
                const data = await response.json();
                console.log("JWT is valid");
                handleMeEndpointOkResponse(data);
            } else {
                console.log("Unable to validate JWT, response: ", response);
                onSetUserContext({
                    isAuthenticated: false,
                });
            }
        }

        return () => {
            ignore = true;
        };
    }, [
        handleMeEndpointOkResponse,
        onSetUserContext,
        userContext.isAuthenticated,
    ]);
}
